import React, { useState, useCallback, useEffect } from 'react';
import { Field, FormSection } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import EditorInfoIcon from '@atlaskit/icon/glyph/editor/info';
import { token } from '@atlaskit/tokens';
import { P200 } from '@atlaskit/theme/colors';
import { FormData } from './types';
// eslint-disable-next-line workspaces/require-dependency
import { getPartnerSupportUrl } from '@css-xp/ppc-navigation-partner-support-utils';
//eslint-disable-next-line
import { SelectCustomerAddress } from '@css-xp/ppc-select-customer-address';
import { FormatCommerceAddress, getCurrencyByCountry, Currency, getReadMoreDialog } from '@css-xp/ppc-common';
import Button from '@atlaskit/button';
import './sold-to-address-step.scss';
import { CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';

interface SoldToAddressStepProps {
  onFieldValueChange: (name: string, value: any) => void;
  onShipToAddressUpdate: (newAddress: CommerceAddress | null | undefined) => void;
  billingProfileData: FormData;
  customerAddresses?: CommerceAddress[];
  defaultSelectedAddress?: CommerceAddress | null | undefined;
  soldToPartiesLoading?: boolean;
  isDefaultAddressSelected?: boolean;
  isMultiCurrencyChangesEnabled?: boolean;
}

export const SoldToAddressStep = ({
  onFieldValueChange,
  onShipToAddressUpdate,
  billingProfileData,
  customerAddresses,
  defaultSelectedAddress,
  soldToPartiesLoading,
  isDefaultAddressSelected,
  isMultiCurrencyChangesEnabled,
}: SoldToAddressStepProps) => {
  const [selectedSoldToAddress, setSelectedSoldToAddress] = useState(defaultSelectedAddress);
  const [isAddressSelected, setIsAddressSelected] = useState(isDefaultAddressSelected);
  const [showCurrencyWarning, setShowCurrencyWarning] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  //if it has only one sold to address then selecting it by default
  useEffect(() => {
    if (customerAddresses?.length === 1) {
      setIsAddressSelected(true);
      setSelectedSoldToAddress(customerAddresses[0]);
      isDefaultAddressSelected = true;
      defaultSelectedAddress = customerAddresses[0];
    }
  }, [customerAddresses]);

  const warningMessage = (
    <>
      Country is linked to the currency associated with this billing profile.{' '}
      <a href={getPartnerSupportUrl()} target="_blank">
        Contact support{' '}
      </a>
      to change the country.
    </>
  );

  const getSelectedAddress = useCallback(() => {
    onShipToAddressUpdate(selectedSoldToAddress);
    return (
      <div className="selected-commerce-address-container">
        <FormatCommerceAddress
          commerceAddress={selectedSoldToAddress}
          showCurrency={isMultiCurrencyChangesEnabled}
          showPricingEligibility={isMultiCurrencyChangesEnabled}
        />
        {!isDefaultAddressSelected && (
          <Button
            appearance="link"
            testId="ppc-change-sold-to-address"
            onClick={() => {
              setIsAddressSelected(false);
            }}
          >
            Change
          </Button>
        )}
      </div>
    );
  }, [selectedSoldToAddress, defaultSelectedAddress]);

  useEffect(() => {
    if (isMultiCurrencyChangesEnabled && selectedSoldToAddress?.country) {
      const currency = getCurrencyByCountry(selectedSoldToAddress.country);
      setShowCurrencyWarning(currency !== Currency.USD);
    }
    onFieldValueChange('soldToAddress', selectedSoldToAddress);
  }, [onFieldValueChange, selectedSoldToAddress, showCurrencyWarning, isMultiCurrencyChangesEnabled]);

  const onSelect = useCallback(
    (selectedAddress: any) => {
      setSelectedSoldToAddress(selectedAddress);
      setIsAddressSelected(true);
      getSelectedAddress();
    },
    [onShipToAddressUpdate, selectedSoldToAddress]
  );

  return (
    <>
      <FormSection>
        <div className="form-component-width">
          <h3>{billingProfileData.invoiceGroupName}</h3>

          <Field
            name="invoiceGroupName"
            id="invoiceGroupName"
            defaultValue={billingProfileData.invoiceGroupName}
            label="Name"
            isRequired={true}
          >
            {({ fieldProps }) => <Textfield {...fieldProps} name="invoiceGroupName" testId="invoice-group-name" />}
          </Field>
        </div>
      </FormSection>
      <FormSection>
        <div className="form-component-width">
          <h3 className={isAddressSelected ? 'sold-to-address-header' : 'sold-to-address-header-not-selected'}>
            Sold-to address
          </h3>
          {isAddressSelected ? (
            <>
              {getSelectedAddress()}
              {isMultiCurrencyChangesEnabled && showCurrencyWarning && (
                <div className="country-message-wrapper">
                  <EditorInfoIcon label="" size="medium" primaryColor={token('color.icon', P200)} />
                  <div className="country-message">
                    {`Currency will be set to ${getCurrencyByCountry(
                      selectedSoldToAddress?.country
                    )} for this sold to address.`}
                  </div>
                </div>
              )}
            </>
          ) : (
            <Field
              label={'Sold-to address'}
              name={'selectCustomerAddress'}
              id={'selectCustomerAddress'}
              isRequired={true}
            >
              {({ fieldProps }) => (
                <SelectCustomerAddress
                  onSelect={onSelect}
                  soldToAddresses={customerAddresses}
                  defaultSoldToAddress={defaultSelectedAddress as CommerceAddress}
                  customClassName="customer-address-select"
                  isLoading={soldToPartiesLoading}
                  isAllSoldToAddressDisabled={true}
                  fieldProps={fieldProps}
                />
              )}
            </Field>
          )}
        </div>
      </FormSection>
    </>
  );
};
